// @ts-strict-ignore
/**
 * Threshold rendering logic.
 *
 * Converts a threshold into a concise, readable sentence using a
 * rules-based dispatcher. The left side is rendered once, then a
 * single right-side rule is applied.
 */
import type { SchemaInstance } from 'flow/store/engine/types';
import { isActionOfType } from '../../actions/actions';
import { ActionType } from 'flow/store/engine/constants';
import { getActionDescriptionForThreshold, getOperatorDescription } from './formatting';
import { generateMLAFootnoteForSide } from './mlaFootnote';
import type { CustomList } from 'flow/service/actions/services/customLists/types';

type ServicesMap = Record<string, any> | undefined;

type Side = 'left' | 'right';

/**
 * Resolves the attribute display name for a given side when the side action is a service.
 */
export function getAttributeDisplayNameForSide({
  services,
  workflow,
  threshold,
  side,
}: {
  services: ServicesMap;
  workflow: Partial<SchemaInstance>;
  threshold: any;
  side: Side;
}): string | null {
  try {
    if (!services) return null;
    const sideData = threshold?.[side];
    const actionId = sideData?.actionId;
    if (!actionId) return null;
    const action = workflow.actions[actionId];
    if (!isActionOfType(action, ActionType.SERVICE)) return null;

    const serviceInternalName = action.service?.internalName;
    const attributeId = sideData?.attributeId;
    if (!serviceInternalName || !attributeId) return null;

    return services[serviceInternalName]?.attributes?.[attributeId]?.display_name ?? null;
  } catch {
    return null;
  }
}

/**
 * Builds the left-hand description including optional attribute and MLA footnote.
 */
export function buildLeftSideDescription({
  services,
  workflow,
  threshold,
  customLists,
}: {
  services: ServicesMap;
  workflow: Partial<SchemaInstance>;
  threshold: any;
  customLists: CustomList[];
}): string {
  const leftAction = workflow.actions[threshold.left.actionId];
  let description = getActionDescriptionForThreshold(leftAction);

  const attributeDisplayName = getAttributeDisplayNameForSide({
    services, workflow, threshold, side: 'left',
  });
  if (attributeDisplayName) {
    description += ` "${attributeDisplayName}" attribute`;
    const leftMlaFootnote = generateMLAFootnoteForSide({
      services,
      actions: workflow.actions,
      threshold,
      side: 'left',
      customLists,
    });
    if (leftMlaFootnote) description += leftMlaFootnote;
  }

  return description;
}

/**
 * Describes a threshold by:
 * 1) Rendering the left side once
 * 2) Selecting the first matching right-side rule to complete the sentence
 */
export function describeThreshold({
  services,
  workflow,
  thresholdId,
  getReasonCodeGroupName,
  customLists,
}: {
  services: ServicesMap;
  workflow: Partial<SchemaInstance>;
  thresholdId: string;
  getReasonCodeGroupName: (id: number) => string;
  customLists: CustomList[];
}): string {
  const threshold = workflow.thresholds[thresholdId];
  if (!threshold) return '';

  const leftAction = workflow.actions[threshold.left.actionId];
  const rightAction = threshold.right.actionId
    ? workflow.actions[threshold.right.actionId]
    : null;

  const leftDescription = buildLeftSideDescription({
    services, workflow, threshold, customLists,
  });

  const rules: Array<{ when: () => boolean; render: () => string }> = [
    {
      when: () => isActionOfType(leftAction, ActionType.TAG)
        && (threshold.right?.leftTagActionResult !== null),
      render: () => (threshold.right.leftTagActionResult ? ' is set' : ' is not set'),
    },
    {
      when: () => Boolean(threshold.right?.reasonCodeGroupId),
      render: () => {
        const groupId = threshold.right.reasonCodeGroupId as number;
        const groupName = getReasonCodeGroupName(groupId);
        return ` is in one of the reason codes in group "${groupName}"`;
      },
    },
    {
      when: () => Boolean(rightAction),
      render: () => {
        const rightActionDesc = getActionDescriptionForThreshold(rightAction);

        let rightAttrDesc = '';
        const displayName = getAttributeDisplayNameForSide({
          services, workflow, threshold, side: 'right',
        });
        if (displayName) {
          rightAttrDesc = ` "${displayName}" attribute`;
          const rightMlaFootnote = generateMLAFootnoteForSide({
            services,
            actions: workflow.actions,
            threshold,
            side: 'right',
            customLists,
          });
          if (rightMlaFootnote) rightAttrDesc += rightMlaFootnote;
        }

        const operatorDesc = getOperatorDescription(threshold.operator);
        return ` is ${operatorDesc} ${rightActionDesc}${rightAttrDesc}`;
      },
    },
    {
      when: () => threshold.right.attributeValue !== null,
      render: () => {
        const operatorDesc = getOperatorDescription(threshold.operator);
        let operatorPhrase = operatorDesc;
        let rightValue: string | number = threshold.right.attributeValue as any;

        if (threshold.operator === 'range' && typeof rightValue === 'string' && rightValue.includes(',')) {
          const [min, max] = rightValue.split(',');
          rightValue = `${min?.trim()} to ${max?.trim()}`;
        }

        if (threshold.operator === 'e' && typeof rightValue === 'string' && rightValue.includes(',')) {
          const parts = rightValue.split(',').map(p => p.trim()).filter(Boolean);
          rightValue = parts.join(', ');
          operatorPhrase = 'equal to one of: ';
        }

        return ` is ${operatorPhrase} ${rightValue}`;
      },
    },
  ];

  for (const rule of rules) {
    if (rule.when()) return leftDescription + rule.render();
  }

  return leftDescription;
}

